import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card, CardContent, Typography, Grid, Box, Button, TextField,
    Switch, FormControlLabel, Alert, CircularProgress, Breadcrumbs, Link
} from '@mui/material';
import { useNotify } from 'react-admin';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import InfoIcon from '@mui/icons-material/Info';
import GroupsIcon from '@mui/icons-material/Groups';
import RestoreIcon from '@mui/icons-material/Restore';
import { motion } from 'framer-motion';
import axios from 'axios';
import config from '../../config/config.json';

const host = config.production.host;
const getJwt = () => localStorage.getItem("feathers-jwt");

export const GruppiEditPage = () => {
    const { gruppoId } = useParams();
    const navigate = useNavigate();
    const notify = useNotify();

    const [formData, setFormData] = React.useState({
        nome: '', note: '', flVisibile: true, flAttivo: true, ordinamento: ''
    });

    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [originalData, setOriginalData] = React.useState({});
    const [hasChanges, setHasChanges] = React.useState(false);

    const loadData = async () => {
        try {
            const jwt = getJwt();
            const response = await axios.get(`${host}/tbl-gruppo/${gruppoId}`, {
                headers: { 'Authorization': `Bearer ${jwt}` }
            });

            const gruppo = response.data;
            const formDataFromGruppo = {
                nome: gruppo.nome || '',
                note: gruppo.note || '',
                flVisibile: gruppo.flVisibile !== undefined ? gruppo.flVisibile : true,
                flAttivo: gruppo.flAttivo !== undefined ? gruppo.flAttivo : true,
                ordinamento: gruppo.ordinamento || ''
            };

            setFormData(formDataFromGruppo);
            setOriginalData(formDataFromGruppo);
        } catch (error) {
            notify("Errore nel caricamento", { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleFieldChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const jwt = getJwt();
            const dataToSend = {
                nome: formData.nome.trim(),
                note: formData.note.trim() || null,
                flVisibile: formData.flVisibile ? 1 : 0,
                flAttivo: formData.flAttivo ? 1 : 0,
                ordinamento: formData.ordinamento ? parseInt(formData.ordinamento) : 0
            };

            await axios.patch(`${host}/tbl-gruppo/${gruppoId}`, dataToSend, {
                headers: { 'Authorization': `Bearer ${jwt}` }
            });
            notify(`Gruppo aggiornato con successo`, { type: 'success' });
            navigate('/gruppi');
        } catch (error) {
            notify("Errore nell'aggiornamento", { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    React.useEffect(() => { loadData(); }, [gruppoId]);
    React.useEffect(() => { setHasChanges(JSON.stringify(formData) !== JSON.stringify(originalData)); }, [formData, originalData]);

    if (loading) return <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                <Breadcrumbs sx={{ mb: 3 }}>
                    <Link component="button" variant="body2" onClick={() => navigate('/gruppi')}>Gruppi</Link>
                    <Typography variant="body2" color="text.primary">Modifica</Typography>
                </Breadcrumbs>

                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        <GroupsIcon sx={{ fontSize: 32, verticalAlign: 'middle', mr: 1 }} />
                        Modifica Gruppo
                    </Typography>
                    <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/gruppi')}>Indietro</Button>
                </Box>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                    <InfoIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Informazioni Gruppo</Typography>
                                </Box>
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <TextField fullWidth label="Nome Gruppo *" value={formData.nome} onChange={(e) => handleFieldChange('nome', e.target.value)} />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField fullWidth label="Ordinamento" type="number" value={formData.ordinamento} onChange={(e) => handleFieldChange('ordinamento', e.target.value)} inputProps={{ min: 0 }} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField fullWidth label="Note" multiline rows={4} value={formData.note} onChange={(e) => handleFieldChange('note', e.target.value)} />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card sx={{ borderRadius: 3, boxShadow: 2, mb: 3 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Impostazioni</Typography>
                                <FormControlLabel control={<Switch checked={formData.flVisibile} onChange={(e) => handleFieldChange('flVisibile', e.target.checked)} />} label="Gruppo visibile" sx={{ mb: 1 }} />
                                <FormControlLabel control={<Switch checked={formData.flAttivo} onChange={(e) => handleFieldChange('flAttivo', e.target.checked)} />} label="Gruppo attivo" />
                            </CardContent>
                        </Card>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Button variant="contained" size="large" startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                onClick={handleSave} disabled={!hasChanges || saving} sx={{ py: 1.5 }}>
                                {saving ? 'Salvataggio...' : 'Salva Modifiche'}
                            </Button>
                            <Button variant="outlined" startIcon={<RestoreIcon />} onClick={() => setFormData(originalData)} disabled={!hasChanges || saving}>
                                Ripristina
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </motion.div>
        </Box>
    );
};
